import useEditRole from 'api/accessControl/useEditRole/useEditRole';
import { useGetRoleList } from 'api/accessControl/useGetRoleList/useGetRoleList';
import useInsertRole from 'api/accessControl/useInsertRole/useInsertRole';
import { EditRoleModal } from 'components/private/Modals/accessControl/EditRoleModal/EditRoleModal';
import RoleInsertModal from 'components/private/Modals/accessControl/RoleInsertModal/RoleInsertModal';
import ApiPaginateTable from 'components/shared/Table/ApiPaginateTable';
import useRolesTableColumns from 'hooks/accessControl/useRoleTableColumns';
import { permissions } from 'hooks/permissions/permissions';
import { usePermissions } from 'hooks/permissions/usePermissions';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import PageSpinner from '../../components/shared/pageSpinner/PageSpinner';

const GROUP_PERMISSION_ENUM = 1; // 0=base role, 1=groupPermission

const RolesPage = () => {
  const navigate = useNavigate();
  const { can } = usePermissions();

  const rolesPermissions =
    permissions.general.categories.keySettings.accessLevel.roles;

  const canCreate = can(rolesPermissions.create);
  const canEdit = can(rolesPermissions.edit);
  const canViewPermissions = can(rolesPermissions.permissions);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState('100');
  const [selectedRows, setSelectedRows] = useState([]);
  const [getRoleList, loading, roleList, pageRef] = useGetRoleList();

  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isInsertModalOpen, setIsInsertModalOpen] = useState(false);
  const [roleEditValues, setRoleEditValues] = useState({});
  const [insertInputValue, setInsertInputValue] = useState('');

  const [editRequest, editLoading] = useEditRole();
  const [insertRequest, insertLoading] = useInsertRole();

  const tableRef = useRef(null);

  useEffect(() => {
    getRoleList({ page: 1, count: pageSize });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    getRoleList({ page, count: pageSize });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageSize]);

  const editItemPermissions = useCallback(
    row => () => {
      navigate(`/roles/${row.id}/permissions-list`);
    },
    [],
  );

  const editItem = useCallback(
    row => () => {
      setRoleEditValues(row);
      setIsEditModalOpen(true);
    },
    [],
  );

  const [columnsData] = useRolesTableColumns(
    editItemPermissions,
    editItem,
    tableRef,
    {
      canEdit,
      canViewPermissions,
    },
  );

  const editHandle = data => {
    if (data) {
      editRequest(
        {
          guid: roleEditValues.id,
          name: data.title,
          display: data.display,
          type: GROUP_PERMISSION_ENUM,
        },
        getRoleList,
        setIsEditModalOpen,
      );
    }
  };

  const insertHandle = payload => {
    insertRequest(
      () => getRoleList({ page, count: pageSize }),
      setIsInsertModalOpen,
      {
        name: payload.name,
        display: payload.display,
        type: GROUP_PERMISSION_ENUM,
      },
    );
  };

  const closeEditModal = () => setIsEditModalOpen(false);

  function getPageData(pageNumber) {
    setPage(pageNumber);
    getRoleList({ page: pageNumber, count: pageSize });
  }

  return (
    <>
      <div>{pageRef.refTitle}</div>

      {loading ? (
        <PageSpinner loading />
      ) : (
        <ApiPaginateTable
          columns={columnsData}
          rowsData={roleList[page]}
          rows={roleList[page]}
          pageSize={pageSize}
          isDeletable={false}
          isLoading={loading}
          addLable={canCreate ? 'ایجاد نقش جدید' : undefined}
          onAddClick={canCreate ? () => setIsInsertModalOpen(true) : undefined}
          setPageSize={val => setPageSize(val)}
          apiRef={tableRef}
          onPageChange={p => getPageData(p)}
          page={page}
          countPages={
            pageRef?.totalCount ? Math.ceil(pageRef.totalCount / pageSize) : 1
          }
          setSelectedRows={rows => setSelectedRows(rows)}
          selectedRows={selectedRows}
        />
      )}

      {canEdit ? (
        <EditRoleModal
          open={isEditModalOpen}
          handleClose={closeEditModal}
          editLoading={editLoading}
          onEdit={editHandle}
          initialValues={roleEditValues}
        />
      ) : null}

      {canCreate ? (
        <RoleInsertModal
          open={isInsertModalOpen}
          handleClose={() => setIsInsertModalOpen(false)}
          insertLoading={insertLoading}
          onInsert={insertHandle}
          insertInputValue={insertInputValue}
          insertInputOnchange={e => setInsertInputValue(e.target.value)}
        />
      ) : null}
    </>
  );
};

export default RolesPage;
